import type { EmptyFeatureResult, SignalStoreFeature } from '@ngrx/signals'
import { getState, signalStoreFeature, withHooks } from '@ngrx/signals'
import { PersistState } from '@awork/core/state/signal-store/persistState'
import type { SignalStore, StoreState } from '@awork/core/state/signal-store/signalStore'
import type { Observable } from 'rxjs'
import { Subject, debounceTime, finalize, map, timeout } from 'rxjs'
import type { EntityPersistenceDelegate, EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import type { Injector } from '@angular/core'
import { computed, DestroyRef } from '@angular/core'
import type { EntityMap } from '@ngrx/signals/entities'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { app } from '@awork/environments/environment'
import type { GenericEntity, SyncFilter } from '@awork/core/state/signal-store/types'
import { isPersistStateStorage, normalizeDateValue, normalizeSyncFilters } from '@awork/core/state/signal-store/helpers'

export const PERSIST_DELAY = app === 'web' ? 200 : 1000

/**
 * Options for the storage:
 * - key: The key used in IndexedDB.
 * - persistDelay: The delay in milliseconds to persist the state to the storage. Used with RxJS's debounceTime.
 */
export interface StorageOptions<StateOrEntity extends object = object> {
  key: string
  persistDelay?: number
  persistActiveState?: boolean
  excludeProps?: (keyof StateOrEntity)[]
}

interface PlainStateChanges {
  upserts?: Record<string, unknown>
  deletes?: string[]
  meta?: PersistedPlainStateMeta
}

interface EntitySignalStoreType<Entity> {
  entityMap: () => EntityMap<Entity>
  ids: () => string[]
  entities: () => Entity[]
}

interface PersistedPlainStateMeta {
  loading: boolean
  error: Error | null
  loadedOn?: Date | null
}

interface PersistedState<State> {
  state: State
  loading: boolean
  error: Error | null
  loadedOn?: Date | null
}

interface PlainStateRestore<State extends object> {
  state: Partial<State>
  obsoleteProperties: string[]
}

interface PlainPersistenceSnapshot {
  state: Record<string, unknown>
  loading: boolean
  error: Error | null
  loadedOn: Date | null
}

interface PlainPersistenceHydrationGuard {
  start(): void
  finish(hydrated: boolean): void
  fail(): void
}

interface RestoreStore {
  reset(): void
  finishRestoreTracking(hydrated: boolean): void
  markAsRestored(): void
}

/**
 * Clears a failed restore and releases every waiter even if cleanup also fails.
 * @param {PersistState} storage
 * @param {RestoreStore} store
 * @param {string} storeName
 * @param {PlainPersistenceHydrationGuard | EntityPersistenceHydrationGuard | undefined} hydrationGuard
 * @param {() => Observable<boolean>} deleteRows
 * @param {unknown} error
 */
function recoverRestore(
  storage: PersistState,
  store: RestoreStore,
  storeName: string,
  hydrationGuard: PlainPersistenceHydrationGuard | EntityPersistenceHydrationGuard | undefined,
  deleteRows: () => Observable<boolean>,
  error: unknown
): void {
  storage.logRestoreError(storeName, error)

  try {
    store.reset()
  } catch (resetError) {
    storage.logRestoreError(storeName, resetError)
  } finally {
    try {
      hydrationGuard?.fail()
    } catch (cleanupError) {
      storage.logRestoreError(storeName, cleanupError)
    }

    store.finishRestoreTracking(false)

    try {
      deleteRows()
        .pipe(
          timeout({ first: 10_000 }),
          finalize(() => store.markAsRestored())
        )
        .subscribe({ error: deleteError => storage.logRestoreError(storeName, deleteError) })
    } catch (deleteError) {
      storage.logRestoreError(storeName, deleteError)
      store.markAsRestored()
    }
  }
}

/**
 * Adds persistence storage capabilities to the signal store.
 * It saves the state to the storage and restores it on initialization.
 * @param {SignalStore<State>} store
 * @param {StorageOptions<State>} storageOptions
 * @param {Injector} injector
 * @returns {SignalStoreFeature<EmptyFeatureResult & { state: State; }, EmptyFeatureResult>}
 * @template State
 */
export function withStateStorage<State extends object = object>(
  store: SignalStore<State>,
  storageOptions: StorageOptions<State>,
  injector: Injector
): SignalStoreFeature<EmptyFeatureResult & { state: State }, EmptyFeatureResult> {
  return signalStoreFeature(
    withHooks({
      onInit(storeState: unknown) {
        const storage = PersistState.instance?.storage

        // Check if storage is available (IndexedDB)
        if (!isPersistStateStorage(storage)) {
          store.finishRestoreTracking(false)
          store.markAsRestored()

          return
        }

        store.beginRestoreTracking()

        let hydrationGuard: PlainPersistenceHydrationGuard | undefined
        const onError = (error: unknown): void => {
          recoverRestore(
            storage,
            store,
            storageOptions.key,
            hydrationGuard,
            () => storage.deleteItem(storageOptions.key),
            error
          )
        }

        try {
          hydrationGuard = persistState(
            storage,
            store,
            storeState as StoreState<State>,
            storageOptions,
            injector,
            onError
          )
          void restoreState(storage, store, storageOptions, hydrationGuard).catch(onError)
        } catch (error) {
          onError(error)
        }
      }
    })
  )
}

/**
 * Adds persistence storage capabilities to the entity signal store.
 * It saves the state to the storage and restores it on initialization.
 * @param {EntitySignalStore<Entity>} entitySignalStore
 * @param {StorageOptions<Entity>} storageOptions
 * @param {Injector} injector
 * @returns {SignalStoreFeature<EmptyFeatureResult, EmptyFeatureResult>}
 */
export function withEntityStorage<Entity extends GenericEntity>(
  entitySignalStore: EntitySignalStore<Entity>,
  storageOptions: StorageOptions<Entity>,
  injector: Injector
): SignalStoreFeature<EmptyFeatureResult, EmptyFeatureResult> {
  return signalStoreFeature(
    withHooks({
      onInit(signalStore: unknown) {
        const storage = PersistState.instance?.storage

        // Check if storage is available (IndexedDB)
        if (!isPersistStateStorage(storage)) {
          entitySignalStore.finishRestoreTracking(false)
          entitySignalStore.markAsRestored()

          return
        }

        entitySignalStore.beginRestoreTracking()

        let hydrationGuard: EntityPersistenceHydrationGuard | undefined
        const onError = (error: unknown): void => {
          recoverRestore(
            storage,
            entitySignalStore,
            storageOptions.key,
            hydrationGuard,
            () => storage.deleteEntityStore(storageOptions.key),
            error
          )
        }

        try {
          hydrationGuard = persistEntityState(
            storage,
            signalStore as EntitySignalStoreType<Entity>,
            entitySignalStore,
            storageOptions,
            injector,
            onError
          )
          void restoreEntityState(storage, entitySignalStore, storageOptions, hydrationGuard).catch(onError)
        } catch (error) {
          onError(error)
        }
      }
    })
  )
}

/**
 * Restores the state from the storage
 * @param {PersistState} storage
 * @param {SignalStore<State>} signalStore
 * @param {StorageOptions<State>} storageOptions
 * @param {PlainPersistenceHydrationGuard} hydrationGuard
 * @returns {Promise<void>}
 */
function restoreState<State extends object>(
  storage: PersistState,
  signalStore: SignalStore<State>,
  storageOptions: StorageOptions<State>,
  hydrationGuard: PlainPersistenceHydrationGuard
): Promise<void> {
  return storage.getItem<PersistedState<State>>(storageOptions.key).then(storedState => {
    if (storedState && signalStore.canApplyPersistedRestore()) {
      hydrationGuard.start()

      const loadedOn = storedState.loading ? null : storedState.loadedOn || new Date()
      const restoredState = filterPersistedState(storedState.state, signalStore.getStoreOptions().initialState)

      signalStore.update(restoredState.state, storageOptions.key + ': restoreState')
      signalStore.setLoading(storedState.loading, loadedOn)
      signalStore.setError(storedState.error)
      hydrationGuard.finish(true)

      if (restoredState.obsoleteProperties.length) {
        storage.applyPlainStateChanges(storageOptions.key, { deletes: restoredState.obsoleteProperties })
      }

      return
    }

    hydrationGuard.finish(false)
  })
}

/**
 * Keeps persisted properties that still exist in the current store schema.
 * @param {State} persistedState State read from persistence.
 * @param {State} initialState Current store schema and default values.
 * @returns {PlainStateRestore<State>} State that can be restored and properties that must be removed from persistence.
 */
function filterPersistedState<State extends object>(
  persistedState: State,
  initialState: State
): PlainStateRestore<State> {
  const currentProperties = new Set(Object.keys(initialState))
  const obsoleteProperties = Object.keys(persistedState).filter(property => !currentProperties.has(property))
  const state = Object.fromEntries(
    Object.entries(persistedState).filter(([property]) => currentProperties.has(property))
  ) as Partial<State>

  return { state, obsoleteProperties }
}

/**
 * Persists the state to the storage
 * @param {PersistState} storage
 * @param {SignalStore<State>} signalStore
 * @param {StoreState<State>} storeState
 * @param {StorageOptions<State>} storageOptions
 * @param {Injector} injector
 * @param {(error: unknown) => void} onError
 * @returns {PlainPersistenceHydrationGuard}
 * @template State
 */
function persistState<State extends object>(
  storage: PersistState,
  signalStore: SignalStore<State>,
  storeState: StoreState<State>,
  storageOptions: StorageOptions<State>,
  injector: Injector,
  onError: (error: unknown) => void
): PlainPersistenceHydrationGuard {
  const dueTime = storageOptions.persistDelay || PERSIST_DELAY
  const snapshot = computed<PlainPersistenceSnapshot>(() =>
    createPlainPersistenceSnapshot(signalStore, storeState, storageOptions)
  )
  const initialSnapshot = snapshot()
  let previousSnapshot = initialSnapshot
  let isHydrating = false
  let hasCompletedRestore = false
  let snapshotRevision = 0
  const destroyRef = injector.get(DestroyRef)

  /**
   * Persists the delta between the previous and current plain-state snapshots.
   * @param {PlainPersistenceSnapshot} currentSnapshot
   */
  function persistCurrentSnapshot(currentSnapshot: PlainPersistenceSnapshot): void {
    const upserts = getChangedPlainStateProperties(previousSnapshot, currentSnapshot)
    const deletes = Object.keys(previousSnapshot.state).filter(key => !(key in currentSnapshot.state))
    const metaChanged = hasPlainStateMetaChanged(previousSnapshot, currentSnapshot)

    if (!Object.keys(upserts).length && !deletes.length && !metaChanged) {
      previousSnapshot = currentSnapshot

      return
    }

    const changes: PlainStateChanges = {
      upserts,
      deletes
    }

    if (metaChanged || Object.keys(upserts).length > 0 || deletes.length > 0) {
      changes.meta = {
        loading: currentSnapshot.loading,
        error: currentSnapshot.error,
        loadedOn: currentSnapshot.loadedOn
      }
    }

    storage.applyPlainStateChanges(storageOptions.key, changes)
    previousSnapshot = currentSnapshot
  }

  queueMicrotask(() => {
    if (destroyRef.destroyed) {
      return
    }

    toObservable(snapshot, { injector })
      .pipe(
        map(currentSnapshot => ({ currentSnapshot, snapshotRevision })),
        debounceTime(dueTime),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(({ currentSnapshot, snapshotRevision: emissionRevision }) => {
        if (emissionRevision !== snapshotRevision || isHydrating || !hasCompletedRestore) {
          return
        }

        persistCurrentSnapshot(currentSnapshot)
      })
  })

  return {
    start(): void {
      isHydrating = true
      signalStore.startRestoreHydration()
    },
    finish(hydrated: boolean): void {
      /**
       * Completes plain-store restore and resumes persistence.
       */
      const completeRestore = (): void => {
        const currentSnapshot = snapshot()

        previousSnapshot = hydrated ? currentSnapshot : initialSnapshot
        hasCompletedRestore = true
        isHydrating = false
        snapshotRevision += 1
        signalStore.finishRestoreTracking(hydrated)
        signalStore.markAsRestored()

        if (!hydrated) {
          persistCurrentSnapshot(currentSnapshot)
        }
      }

      if (hydrated) {
        queueMicrotask(() => {
          try {
            completeRestore()
          } catch (error) {
            onError(error)
          }
        })

        return
      }

      completeRestore()
    },
    fail(): void {
      previousSnapshot = { ...initialSnapshot, state: {} }
      hasCompletedRestore = true
      isHydrating = false
      snapshotRevision += 1
    }
  }
}

/**
 * Restores the state from the storage
 * @param {PersistState} storage
 * @param {EntitySignalStore<Entity>} entitySignalStore
 * @param {StorageOptions<Entity>} storageOptions
 * @param {EntityPersistenceHydrationGuard} hydrationGuard
 * @returns {Promise<void>}
 */
async function restoreEntityState<Entity extends GenericEntity>(
  storage: PersistState,
  entitySignalStore: EntitySignalStore<Entity>,
  storageOptions: StorageOptions<Entity>,
  hydrationGuard: EntityPersistenceHydrationGuard
): Promise<void> {
  const storedState = await storage.getEntityState<Entity>(storageOptions.key)
  const isLoading = entitySignalStore.getIsLoading()

  if (storedState && isLoading() && entitySignalStore.canApplyPersistedRestore()) {
    hydrationGuard.start()

    const loadedOn = storedState.loading ? null : normalizeDateValue(storedState.loadedOn) || new Date()

    const restoredEntities = storedState.entities.map(entity => purgeEntity(entity, storageOptions.excludeProps))

    entitySignalStore.set(restoredEntities, storageOptions.key + ': restoreEntityState')
    entitySignalStore.setLoading(storedState.loading, loadedOn)
    entitySignalStore.setError(storedState.error)
    entitySignalStore.setLastSync(normalizeDateValue(storedState.lastSync))
    entitySignalStore.setSyncFilters(normalizeSyncFilters(storedState.syncFilters))

    if (storageOptions.persistActiveState) {
      entitySignalStore.setActive(storedState.active)
    }

    hydrationGuard.finish(true)

    return
  }

  hydrationGuard.finish(false)
}

interface EntityPersistenceMetaSnapshot {
  active: string | null
  loading: boolean
  error: Error | null
  lastSync: Date | null
  loadedOn: Date | null
  syncFilters: SyncFilter[]
}

interface EntityPersistenceHydrationGuard {
  start(): void
  finish(hydrated: boolean): void
  fail(): void
}

interface EntityPendingChanges<Entity> {
  upserts: Entity[]
  deletes: string[]
  meta: EntityPersistenceMetaSnapshot
}

interface EntityPersistenceState<Entity> {
  persistedEntityRows: Map<string, Entity>
  persistedMeta: EntityPersistenceMetaSnapshot
  queuedEntityIds: Set<string>
  dirtyEntityIds: Set<string>
  deletedEntityIds: Set<string>
  metaDirty: boolean
  isHydrating: boolean
  hasCompletedRestore: boolean
  isFlushing: boolean
  persistRequests$: Subject<void>
}

interface EntityPersistenceContext<Entity extends GenericEntity> {
  storage: PersistState
  entitySignalStore: EntitySignalStoreType<Entity>
  store: EntitySignalStore<Entity>
  storageOptions: StorageOptions<Entity>
  state: EntityPersistenceState<Entity>
}

/**
 * Creates a persistence snapshot for a plain signal store.
 * @param {SignalStore<State>} signalStore
 * @param {StoreState<State>} storeState
 * @param {StorageOptions<State>} storageOptions
 * @returns {PlainPersistenceSnapshot}
 * @template State
 */
function createPlainPersistenceSnapshot<State extends object>(
  signalStore: SignalStore<State>,
  storeState: StoreState<State>,
  storageOptions: StorageOptions<State>
): PlainPersistenceSnapshot {
  const state = purgeState(getState(storeState), storageOptions.excludeProps)
  const isLoading = signalStore.getIsLoading()
  const error = signalStore.getError()
  const loadedOn = signalStore.getLoadedOn()

  return {
    state,
    loading: isLoading(),
    error: error(),
    loadedOn: loadedOn()
  }
}

/**
 * Creates a metadata snapshot for an entity signal store.
 * @param {EntitySignalStore<Entity>} store
 * @param {boolean} persistActiveState
 * @returns {EntityPersistenceMetaSnapshot}
 */
function createEntityPersistenceMetaSnapshot<Entity extends GenericEntity>(
  store: EntitySignalStore<Entity>,
  persistActiveState?: boolean
): EntityPersistenceMetaSnapshot {
  const active = store.getActive()
  const isLoading = store.getIsLoading()
  const error = store.getError()
  const lastSync = store.getLastSync()
  const loadedOn = store.getLoadedOn()
  const syncFilters = store.getSyncFilters()

  return {
    active: persistActiveState ? active() : null,
    loading: isLoading(),
    error: error(),
    lastSync: normalizeDateValue(lastSync()),
    loadedOn: normalizeDateValue(loadedOn()),
    syncFilters: normalizeSyncFilters(syncFilters())
  }
}

/**
 * Sets up debounced persistence for an entity signal store.
 * @param {PersistState} storage
 * @param {EntitySignalStoreType<Entity>} entitySignalStore
 * @param {EntitySignalStore<Entity>} store
 * @param {StorageOptions<Entity>} storageOptions
 * @param {Injector} injector
 * @param {(error: unknown) => void} onError
 * @returns {EntityPersistenceHydrationGuard}
 */
function persistEntityState<Entity extends GenericEntity>(
  storage: PersistState,
  entitySignalStore: EntitySignalStoreType<Entity>,
  store: EntitySignalStore<Entity>,
  storageOptions: StorageOptions<Entity>,
  injector: Injector,
  onError: (error: unknown) => void
): EntityPersistenceHydrationGuard {
  const dueTime = storageOptions.persistDelay || PERSIST_DELAY
  const context = createEntityPersistenceContext(storage, entitySignalStore, store, storageOptions)
  const persistenceDelegate = createEntityPersistenceDelegate(context)

  store.registerPersistenceDelegate(persistenceDelegate)
  context.state.persistRequests$
    .pipe(debounceTime(dueTime), takeUntilDestroyed(injector.get(DestroyRef)))
    .subscribe(function flushQueuedEntityChanges(): void {
      void flushPendingEntityChanges(context)
    })

  return createEntityPersistenceHydrationGuard(context, onError)
}

/**
 * Creates the mutable entity persistence context used across flushes and restore.
 * @param {PersistState} storage
 * @param {EntitySignalStoreType<Entity>} entitySignalStore
 * @param {EntitySignalStore<Entity>} store
 * @param {StorageOptions<Entity>} storageOptions
 * @returns {EntityPersistenceContext<Entity>}
 */
function createEntityPersistenceContext<Entity extends GenericEntity>(
  storage: PersistState,
  entitySignalStore: EntitySignalStoreType<Entity>,
  store: EntitySignalStore<Entity>,
  storageOptions: StorageOptions<Entity>
): EntityPersistenceContext<Entity> {
  return {
    storage,
    entitySignalStore,
    store,
    storageOptions,
    state: {
      persistedEntityRows: createPersistedEntityRowMap(entitySignalStore.entities(), storageOptions.excludeProps),
      persistedMeta: createEntityPersistenceMetaSnapshot(store, storageOptions.persistActiveState),
      queuedEntityIds: new Set<string>(entitySignalStore.ids()),
      dirtyEntityIds: new Set<string>(),
      deletedEntityIds: new Set<string>(),
      metaDirty: false,
      isHydrating: false,
      hasCompletedRestore: false,
      isFlushing: false,
      persistRequests$: new Subject<void>()
    }
  }
}

/**
 * Creates the entity persistence delegate that records mutation-time changes.
 * @param {EntityPersistenceContext<Entity>} context
 * @returns {EntityPersistenceDelegate}
 */
function createEntityPersistenceDelegate<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>
): EntityPersistenceDelegate {
  return {
    replace(ids: string[]): void {
      if (context.state.isHydrating) {
        return
      }

      recordReplacedEntityIds(context, ids)
      scheduleEntityPersist(context.state)
    },
    upsert(ids: string[]): void {
      if (context.state.isHydrating) {
        return
      }

      recordUpsertedEntityIds(context.state, ids)
      scheduleEntityPersist(context.state)
    },
    delete(ids: string[]): void {
      if (context.state.isHydrating) {
        return
      }

      recordDeletedEntityIds(context, ids)
      scheduleEntityPersist(context.state)
    },
    metaChange(): void {
      if (context.state.isHydrating) {
        return
      }

      context.state.metaDirty = true
      scheduleEntityPersist(context.state)
    },
    reset(): void {
      resetEntityPersistence(context)
    }
  }
}

/**
 * Creates the restore guard for entity persistence.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {(error: unknown) => void} onError
 * @returns {EntityPersistenceHydrationGuard}
 */
function createEntityPersistenceHydrationGuard<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  onError: (error: unknown) => void
): EntityPersistenceHydrationGuard {
  return {
    start(): void {
      context.state.isHydrating = true
      context.store.startRestoreHydration()
    },
    finish(hydrated: boolean): void {
      if (hydrated) {
        queueMicrotask(function completeEntityRestore(): void {
          try {
            finishEntityPersistenceRestore(context, hydrated)
            context.store.finishRestoreTracking(hydrated)
            context.store.markAsRestored()
          } catch (error) {
            onError(error)
          }
        })

        return
      }

      finishEntityPersistenceRestore(context, hydrated)
      context.store.finishRestoreTracking(hydrated)
      context.store.markAsRestored()
    },
    fail(): void {
      clearQueuedEntityChanges(context.state)
      context.state.hasCompletedRestore = true
      context.state.isHydrating = false
    }
  }
}

/**
 * Finishes entity-store restore and resumes persistence.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {boolean} hydrated
 */
function finishEntityPersistenceRestore<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  hydrated: boolean
): void {
  if (hydrated) {
    clearQueuedEntityChanges(context.state)
    syncPersistedEntityBaseline(context)
  }

  context.state.hasCompletedRestore = true
  context.state.isHydrating = false

  if (!hydrated && hasQueuedEntityChanges(context.state)) {
    scheduleEntityPersist(context.state)
  }
}

/**
 * Resets the entity persistence baseline after a full store reset.
 * @param {EntityPersistenceContext<Entity>} context
 */
function resetEntityPersistence<Entity extends GenericEntity>(context: EntityPersistenceContext<Entity>): void {
  clearQueuedEntityChanges(context.state)
  context.state.persistedEntityRows.clear()
  context.state.persistedMeta = createEntityPersistenceMetaSnapshot(
    context.store,
    context.storageOptions.persistActiveState
  )
  context.state.queuedEntityIds = new Set<string>()
}

/**
 * Determines whether entity persistence currently has queued work.
 * @param {EntityPersistenceState<Entity>} state
 * @returns {boolean}
 */
function hasQueuedEntityChanges<Entity>(state: EntityPersistenceState<Entity>): boolean {
  return state.dirtyEntityIds.size > 0 || state.deletedEntityIds.size > 0 || state.metaDirty
}

/**
 * Clears queued entity persistence work.
 * @param {EntityPersistenceState<Entity>} state
 */
function clearQueuedEntityChanges<Entity>(state: EntityPersistenceState<Entity>): void {
  state.dirtyEntityIds.clear()
  state.deletedEntityIds.clear()
  state.metaDirty = false
}

/**
 * Schedules a debounced entity persistence flush when restore already completed.
 * @param {EntityPersistenceState<Entity>} state
 */
function scheduleEntityPersist<Entity>(state: EntityPersistenceState<Entity>): void {
  if (!state.hasCompletedRestore || state.isHydrating) {
    return
  }

  state.persistRequests$.next()
}

/**
 * Syncs the persisted baseline with the store's current runtime state.
 * @param {EntityPersistenceContext<Entity>} context
 */
function syncPersistedEntityBaseline<Entity extends GenericEntity>(context: EntityPersistenceContext<Entity>): void {
  context.state.persistedEntityRows = createPersistedEntityRowMap(
    context.entitySignalStore.entities(),
    context.storageOptions.excludeProps
  )
  context.state.persistedMeta = createEntityPersistenceMetaSnapshot(
    context.store,
    context.storageOptions.persistActiveState
  )
  context.state.queuedEntityIds = new Set<string>(context.entitySignalStore.ids())
}

/**
 * Records a store replacement so persistence can delete removed rows without scanning the whole store.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {string[]} ids
 */
function recordReplacedEntityIds<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  ids: string[]
): void {
  const nextIds = new Set<string>(ids.filter(Boolean))

  context.state.queuedEntityIds.forEach(queuedId => {
    if (nextIds.has(queuedId)) {
      return
    }

    context.state.dirtyEntityIds.delete(queuedId)

    if (context.state.persistedEntityRows.has(queuedId)) {
      context.state.deletedEntityIds.add(queuedId)

      return
    }

    context.state.deletedEntityIds.delete(queuedId)
  })

  nextIds.forEach(id => {
    context.state.dirtyEntityIds.add(id)
    context.state.deletedEntityIds.delete(id)
  })

  context.state.queuedEntityIds = nextIds
}

/**
 * Records entity upserts for later persistence.
 * @param {EntityPersistenceState<Entity>} state
 * @param {string[]} ids
 */
function recordUpsertedEntityIds<Entity>(state: EntityPersistenceState<Entity>, ids: string[]): void {
  ids.filter(Boolean).forEach(id => {
    state.dirtyEntityIds.add(id)
    state.deletedEntityIds.delete(id)
    state.queuedEntityIds.add(id)
  })
}

/**
 * Records entity deletions for later persistence.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {string[]} ids
 */
function recordDeletedEntityIds<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  ids: string[]
): void {
  ids.filter(Boolean).forEach(id => {
    context.state.queuedEntityIds.delete(id)
    context.state.dirtyEntityIds.delete(id)

    if (context.state.persistedEntityRows.has(id)) {
      context.state.deletedEntityIds.add(id)

      return
    }

    context.state.deletedEntityIds.delete(id)
  })
}

/**
 * Requeues a failed persistence attempt so it can be retried later.
 * @param {EntityPersistenceState<Entity>} state
 * @param {Entity[]} upserts
 * @param {string[]} deletes
 * @param {boolean} shouldRequeueMeta
 */
function requeueEntityPersistenceChanges<Entity extends GenericEntity>(
  state: EntityPersistenceState<Entity>,
  upserts: Entity[],
  deletes: string[],
  shouldRequeueMeta: boolean
): void {
  upserts.forEach(entity => {
    state.dirtyEntityIds.add(entity.id)
    state.deletedEntityIds.delete(entity.id)
    state.queuedEntityIds.add(entity.id)
  })

  deletes.forEach(id => {
    state.dirtyEntityIds.delete(id)
    state.queuedEntityIds.delete(id)
    state.deletedEntityIds.add(id)
  })

  state.metaDirty = shouldRequeueMeta || state.metaDirty
}

/**
 * Builds the next queued IndexedDB write from mutation-time dirty tracking.
 * @param {EntityPersistenceContext<Entity>} context
 * @returns {EntityPendingChanges<Entity> | null}
 */
function buildPendingEntityChanges<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>
): EntityPendingChanges<Entity> | null {
  if (!hasQueuedEntityChanges(context.state)) {
    return null
  }

  const pendingDirtyIds = [...context.state.dirtyEntityIds]
  const pendingDeletes = [...context.state.deletedEntityIds]
  const shouldPersistMeta = context.state.metaDirty
  const currentEntityMap = context.entitySignalStore.entityMap()

  pendingDirtyIds.forEach(id => context.state.dirtyEntityIds.delete(id))
  pendingDeletes.forEach(id => context.state.deletedEntityIds.delete(id))
  context.state.metaDirty = false

  const upserts = collectChangedEntityRows(context, currentEntityMap, pendingDirtyIds, pendingDeletes)
  const meta = createEntityPersistenceMetaSnapshot(context.store, context.storageOptions.persistActiveState)
  const deletes = [...new Set(pendingDeletes)]

  if (!upserts.length && !deletes.length && !shouldPersistMeta) {
    return null
  }

  return { upserts, deletes, meta }
}

/**
 * Collects changed entity rows for the next persistence flush.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {EntityMap<Entity>} currentEntityMap
 * @param {string[]} pendingDirtyIds
 * @param {string[]} pendingDeletes
 * @returns {Entity[]}
 */
function collectChangedEntityRows<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  currentEntityMap: EntityMap<Entity>,
  pendingDirtyIds: string[],
  pendingDeletes: string[]
): Entity[] {
  const changedEntities: Entity[] = []

  pendingDirtyIds.forEach(entityId => {
    const currentEntity = currentEntityMap[entityId]

    if (!currentEntity) {
      if (context.state.persistedEntityRows.has(entityId) && !pendingDeletes.includes(entityId)) {
        pendingDeletes.push(entityId)
      }

      return
    }

    const purgedEntity = purgeEntity(currentEntity, context.storageOptions.excludeProps)

    changedEntities.push(purgedEntity)
  })

  return changedEntities
}

/**
 * Applies a successful persistence flush to the in-memory baseline.
 * @param {EntityPersistenceContext<Entity>} context
 * @param {EntityPendingChanges<Entity>} persistedChanges
 */
function applyPersistedEntityChanges<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>,
  persistedChanges: EntityPendingChanges<Entity>
): void {
  persistedChanges.deletes.forEach(entityId => {
    context.state.persistedEntityRows.delete(entityId)
  })

  persistedChanges.upserts.forEach(entity => {
    context.state.persistedEntityRows.set(entity.id, entity)
  })

  context.state.persistedMeta = persistedChanges.meta
  context.state.queuedEntityIds = new Set<string>(context.entitySignalStore.ids())
}

/**
 * Flushes queued entity persistence work using the configured debounce window.
 * @param {EntityPersistenceContext<Entity>} context
 * @returns {Promise<void>}
 */
async function flushPendingEntityChanges<Entity extends GenericEntity>(
  context: EntityPersistenceContext<Entity>
): Promise<void> {
  if (context.state.isFlushing || context.state.isHydrating || !context.state.hasCompletedRestore) {
    return
  }

  context.state.isFlushing = true

  try {
    let pendingChanges = buildPendingEntityChanges(context)

    while (pendingChanges) {
      const previousPersistedEntityRows = new Map<string, Entity>(context.state.persistedEntityRows)
      const previousPersistedMeta = context.state.persistedMeta

      applyPersistedEntityChanges(context, pendingChanges)

      try {
        await context.storage.applyEntityChanges(context.storageOptions.key, pendingChanges)
      } catch {
        context.state.persistedEntityRows = previousPersistedEntityRows
        context.state.persistedMeta = previousPersistedMeta
        requeueEntityPersistenceChanges(context.state, pendingChanges.upserts, pendingChanges.deletes, true)
        break
      }

      pendingChanges = buildPendingEntityChanges(context)
    }
  } finally {
    context.state.isFlushing = false

    if (hasQueuedEntityChanges(context.state)) {
      scheduleEntityPersist(context.state)
    }
  }
}

/**
 * Builds the persisted-entity baseline map for the current store state.
 * @param {Entity[]} entities
 * @param {(keyof Entity)[]} excludeProps
 * @returns {Map<string, Entity>}
 */
function createPersistedEntityRowMap<Entity extends GenericEntity>(
  entities: Entity[],
  excludeProps?: (keyof Entity)[]
): Map<string, Entity> {
  return new Map<string, Entity>(entities.map(entity => [entity.id, purgeEntity(entity, excludeProps)]))
}

/**
 * Purges excluded properties from an entity before persistence and on restore. Rows persisted before a
 * property was excluded still contain it.
 * @param {Entity} entity
 * @param {(keyof Entity)[]} excludeProps
 * @returns {Entity}
 */
function purgeEntity<Entity extends GenericEntity>(entity: Entity, excludeProps?: (keyof Entity)[]): Entity {
  if (!excludeProps?.length) {
    return entity
  }

  // Persisted rows usually already exclude these fields. Avoid cloning the whole cache again.
  if (!excludeProps.some(prop => Object.hasOwn(entity, prop))) {
    return entity
  }

  const purgedEntity = { ...entity }

  excludeProps.forEach(prop => {
    delete purgedEntity[prop]
  })

  return purgedEntity
}

/**
 * Purges excluded properties from a plain state before persistence.
 * @param {State} state
 * @param {(keyof State)[]} excludeProps
 * @returns {Record<string, unknown>}
 * @template State
 */
function purgeState<State extends object>(state: State, excludeProps?: (keyof State)[]): Record<string, unknown> {
  if (!excludeProps?.length) {
    return state as Record<string, unknown>
  }

  const purgedState = { ...state } as Record<string, unknown>

  excludeProps.forEach(prop => {
    delete purgedState[prop as string]
  })

  return purgedState
}

/**
 * Gets changed properties for a plain persisted store.
 * @param {PlainPersistenceSnapshot} previousSnapshot
 * @param {PlainPersistenceSnapshot} currentSnapshot
 * @returns {Record<string, unknown>}
 */
function getChangedPlainStateProperties(
  previousSnapshot: PlainPersistenceSnapshot,
  currentSnapshot: PlainPersistenceSnapshot
): Record<string, unknown> {
  return Object.entries(currentSnapshot.state).reduce<Record<string, unknown>>((changedProperties, [key, value]) => {
    if (previousSnapshot.state[key] === value) {
      return changedProperties
    }

    changedProperties[key] = value

    return changedProperties
  }, {})
}

/**
 * Determines if plain-state metadata changed.
 * @param {PlainPersistenceSnapshot} previousSnapshot
 * @param {PlainPersistenceSnapshot} currentSnapshot
 * @returns {boolean}
 */
function hasPlainStateMetaChanged(
  previousSnapshot: PlainPersistenceSnapshot,
  currentSnapshot: PlainPersistenceSnapshot
): boolean {
  return (
    previousSnapshot.loading !== currentSnapshot.loading ||
    previousSnapshot.error !== currentSnapshot.error ||
    previousSnapshot.loadedOn !== currentSnapshot.loadedOn
  )
}
